import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import { AlertTriangle } from "lucide-react";

export default function Disclaimer() {
  const { t } = useTranslation();
  const { lang = 'de' } = useParams();
  const { getPath } = useLocalizedPath();

  return (
    <div data-testid="disclaimer-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h1 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white">
            {t('disclaimer.title')}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 bg-[#F7FAFC]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Card className="bg-white border">
            <CardContent className="p-6 md:p-10">
              <div className="prose prose-gray max-w-none">
                {/* Hinweis für nicht-deutsche Versionen */}
                {lang !== 'de' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-8">
                    <p className="text-gray-700 text-sm m-0">
                      {t('disclaimer.legalNote')}
                    </p>
                  </div>
                )}

                {/* Haftung für Inhalte */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="h-6 w-6 text-orange-500" />
                    <h2 className="text-2xl font-bold font-['Manrope'] text-[#2D3748] m-0">
                      {t('disclaimer.contentLiability.title')}
                    </h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {t('disclaimer.contentLiability.description1')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('disclaimer.contentLiability.description2')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('disclaimer.contentLiability.description3')}
                  </p>
                </div>

                {/* Haftung für Links */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('disclaimer.linkLiability.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('disclaimer.linkLiability.description1')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('disclaimer.linkLiability.description2')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('disclaimer.linkLiability.description3')}
                  </p>
                </div>

                {/* Urheberrecht */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('disclaimer.copyright.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('disclaimer.copyright.description1')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('disclaimer.copyright.description2')}
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {t('disclaimer.copyright.description3')}
                  </p>
                </div>

                {/* Informationszweck */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('disclaimer.purpose.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('disclaimer.purpose.description')}
                  </p>
                </div>

                {/* Keine Rechtsberatung */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('disclaimer.noAdvice.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('disclaimer.noAdvice.description')}
                  </p>
                </div>

                {/* Änderungen */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('disclaimer.changes.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('disclaimer.changes.description')}
                  </p>
                </div>

                {/* Gültigkeit */}
                <div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('disclaimer.validity.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('disclaimer.validity.description')}
                  </p>
                  <p className="text-gray-600 text-sm mt-4">
                    {t('disclaimer.lastUpdated')}
                  </p>
                </div>
              </div>

              {/* Links */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <p className="text-gray-600 text-sm">
                  {t('impressum.moreInfo')}{" "}
                  <Link to={getPath('impressum')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('impressum.title')}
                  </Link>
                  ,{" "}
                  <Link to={getPath('privacy')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('privacy.title')}
                  </Link>
                  {" "}&{" "}
                  <Link to={getPath('cookies')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('cookiePolicy.title')}
                  </Link>
                  .
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
